// アカウント設定画面(設計書 §3.4.2)
// プロフィール編集 / アカウント情報表示 / パスワード変更 / 統計

import { useEffect, useState } from 'react';
import { ApiClientError } from '../api/client';
import { useMe, useUpdateMe, useUpdatePassword } from '../api/me';
import { useAuth } from '../auth/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ja-JP', { dateStyle: 'medium', timeStyle: 'short' });
}

function formatDuration(seconds: number): string {
  if (!seconds) return '0 分';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h} 時間 ${m} 分`;
  return `${m} 分`;
}

export function SettingsPage() {
  const meQuery = useMe();
  const updateMe = useUpdateMe();
  const updatePassword = useUpdatePassword();
  const { updatePerformerLocal } = useAuth();

  return (
    <AppLayout title="設定" description="アカウント情報とプロフィールを管理します。">
      {meQuery.isLoading && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}
      {meQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {meQuery.error instanceof ApiClientError
              ? meQuery.error.message
              : '読み込みに失敗しました'}
          </p>
        </Card>
      )}
      {meQuery.data && (
        <>
          <ProfileSection
            initialDisplayName={meQuery.data.display_name}
            onSave={async (display_name) => {
              const res = await updateMe.mutateAsync({ display_name });
              updatePerformerLocal({ display_name: res.display_name });
            }}
            saving={updateMe.isPending}
            error={
              updateMe.error instanceof ApiClientError
                ? updateMe.error.message
                : updateMe.error
                  ? '更新に失敗しました'
                  : null
            }
          />
          <AccountInfoSection
            email={meQuery.data.email}
            createdAt={meQuery.data.created_at}
            lastLoginAt={meQuery.data.last_login_at}
            plan={meQuery.data.plan}
            status={meQuery.data.status}
          />
          <GoogleLinkSection
            email={meQuery.data.email}
            googleLinked={meQuery.data.google_linked}
          />
          <StatsSection
            totalStreamingSeconds={meQuery.data.total_streaming_seconds}
            totalSessionCount={meQuery.data.total_session_count}
          />
          <PasswordSection
            onSubmit={(input) => updatePassword.mutateAsync(input)}
            submitting={updatePassword.isPending}
            error={
              updatePassword.error instanceof ApiClientError
                ? updatePassword.error.message
                : updatePassword.error
                  ? '更新に失敗しました'
                  : null
            }
            success={updatePassword.isSuccess}
          />
        </>
      )}
    </AppLayout>
  );
}

interface ProfileSectionProps {
  initialDisplayName: string;
  onSave: (displayName: string) => Promise<void>;
  saving: boolean;
  error: string | null;
}

function ProfileSection({ initialDisplayName, onSave, saving, error }: ProfileSectionProps) {
  const [value, setValue] = useState(initialDisplayName);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(initialDisplayName);
  }, [initialDisplayName]);

  const dirty = value.trim() !== initialDisplayName && value.trim().length > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    setSaved(false);
    await onSave(value.trim());
    setSaved(true);
  }

  return (
    <Card title="プロフィール" description="表示名は配信先プロファイルや管理画面で参照されます。">
      <form onSubmit={handleSubmit}>
        <Input
          label="表示名"
          value={value}
          maxLength={100}
          onChange={(e) => {
            setValue(e.target.value);
            setSaved(false);
          }}
        />
        {error && (
          <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--color-error-text)' }}>
            {error}
          </p>
        )}
        {saved && !error && (
          <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--color-success-text)' }}>
            保存しました
          </p>
        )}
        <div style={{ maxWidth: 200 }}>
          <Button type="submit" disabled={!dirty || saving}>
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface AccountInfoSectionProps {
  email: string;
  createdAt: string;
  lastLoginAt: string | null;
  plan: 'free' | 'pro';
  status: 'pending' | 'active' | 'disabled';
}

function AccountInfoSection({
  email,
  createdAt,
  lastLoginAt,
  plan,
  status,
}: AccountInfoSectionProps) {
  return (
    <Card title="アカウント情報">
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 10 }}>
        <Term>メールアドレス</Term>
        <Detail>{email}</Detail>

        <Term>プラン</Term>
        <Detail style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{plan}</Detail>

        <Term>ステータス</Term>
        <Detail>{status}</Detail>

        <Term>登録日</Term>
        <Detail>{formatDateTime(createdAt)}</Detail>

        <Term>最終ログイン</Term>
        <Detail>{formatDateTime(lastLoginAt)}</Detail>
      </dl>
    </Card>
  );
}

// Google アカウント連携セクション(設計書 §3.4.1 の 2要素フロー拡張)。
//
// 現状の連携フロー:
// - 「事前連携」エンドポイントは未実装。
// - ログイン画面で Google ログインを使うと、メール一致 + パスワード確認の
//   2要素フローで自動的に google_sub が DB に紐付く(/v1/auth/google +
//   /v1/auth/google/confirm-link)。
// - 解除エンドポイントも未実装(後続フェーズで追加予定)。
//
// このセクションでは、現在の連携状態を表示し、未連携時には「次回ログインで
// Google ボタンを使えば自動連携される」旨をユーザーに案内する。
//
// `google_linked` は /v1/me レスポンス(performer.google_sub IS NOT NULL)
// から取得。
interface GoogleLinkSectionProps {
  email: string;
  googleLinked: boolean;
}

function GoogleLinkSection({ email, googleLinked }: GoogleLinkSectionProps) {
  return (
    <Card
      title="Google アカウント連携"
      description="Google アカウントでログインできるようにする設定です。"
    >
      {googleLinked ? (
        <>
          <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--color-text-primary)' }}>
            <span style={{ color: 'var(--color-success-text)', fontWeight: 500 }}>連携済み</span>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {' '}— このアカウント({email})は Google ログインで利用できます。
            </span>
          </p>
          <p
            style={{
              margin: 0,
              fontSize: '11px',
              color: 'var(--color-text-tertiary)',
              lineHeight: 1.6,
            }}
          >
            連携の解除はサポートまでお問い合わせください(設定画面からの解除は
            後続フェーズで対応予定)。
          </p>
        </>
      ) : (
        <>
          <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--color-text-primary)' }}>
            <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 500 }}>未連携</span>
          </p>
          <p
            style={{
              margin: 0,
              fontSize: '11px',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.6,
            }}
          >
            次回ログイン時に「Google でログイン」ボタンを押し、登録メール
            ({email})と一致する Google アカウントで認証 → SafeLens の現在の
            パスワードで連携を確定すると、以降は Google ログインで利用できる
            ようになります。設定画面からの「今すぐ連携」は後続フェーズで実装
            予定です。
          </p>
        </>
      )}
    </Card>
  );
}

interface StatsSectionProps {
  totalStreamingSeconds: number;
  totalSessionCount: number;
}

function StatsSection({ totalStreamingSeconds, totalSessionCount }: StatsSectionProps) {
  return (
    <Card title="累計統計">
      <div style={{ display: 'flex', gap: 32 }}>
        <Stat label="累計配信時間" value={formatDuration(totalStreamingSeconds)} />
        <Stat label="累計セッション数" value={String(totalSessionCount)} />
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: '10px',
          color: 'var(--color-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--color-text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return (
    <dt style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{children}</dt>
  );
}

function Detail({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <dd
      style={{
        margin: 0,
        fontSize: '12px',
        color: 'var(--color-text-primary)',
        ...style,
      }}
    >
      {children}
    </dd>
  );
}

interface PasswordSectionProps {
  onSubmit: (input: { current_password: string; new_password: string }) => Promise<void>;
  submitting: boolean;
  error: string | null;
  success: boolean;
}

function PasswordSection({ onSubmit, submitting, error, success }: PasswordSectionProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (next.length < 8) {
      setLocalError('新しいパスワードは 8 文字以上にしてください');
      return;
    }
    if (next !== confirm) {
      setLocalError('確認用パスワードが一致しません');
      return;
    }
    try {
      await onSubmit({ current_password: current, new_password: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      setSubmittedAt(Date.now());
    } catch {
      // mutation の error は親側で表示
    }
  }

  // success フラグはミューテーションが完了したら立つ。submittedAt を見て直近の成功だけ表示。
  const showSuccess = success && submittedAt !== null && Date.now() - submittedAt < 60_000;

  return (
    <Card
      title="パスワード変更"
      description="新しいパスワードは 8 文字以上で、現在のパスワードと異なるものを指定してください。"
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="現在のパスワード"
          type="password"
          value={current}
          autoComplete="current-password"
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label="新しいパスワード"
          type="password"
          value={next}
          autoComplete="new-password"
          onChange={(e) => setNext(e.target.value)}
        />
        <Input
          label="新しいパスワード(確認)"
          type="password"
          value={confirm}
          autoComplete="new-password"
          onChange={(e) => setConfirm(e.target.value)}
        />
        {(localError ?? error) && (
          <p
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              color: 'var(--color-error-text)',
            }}
          >
            {localError ?? error}
          </p>
        )}
        {showSuccess && !localError && !error && (
          <p
            style={{
              margin: '0 0 12px',
              fontSize: '11px',
              color: 'var(--color-success-text)',
            }}
          >
            パスワードを変更しました
          </p>
        )}
        <div style={{ maxWidth: 240 }}>
          <Button
            type="submit"
            variant="secondary"
            disabled={submitting || !current || !next || !confirm}
          >
            {submitting ? '変更中…' : 'パスワードを変更'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
